import { useCallback, useEffect, useState } from "react";

import { getOrders, OrdersListFilter } from "services/orders/rest";
import { OrderListDto } from "services/orders/restModels";

export interface UseOrdersResult {
    orders: OrderListDto[];
    total: number;
    loading: boolean;
    error: boolean;
    refetch: () => void;
}

/*
* Пагинация серверная (skip/limit, как во всех списках проекта): окно и фильтры держит
* вызывающая фича, хук тянет страницу и общее количество.
* */
export const useOrders = (
    filter: OrdersListFilter | null,
    skip: number,
    limit: number
): UseOrdersResult => {
    const [orders, setOrders] = useState<OrderListDto[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState<number>(0);

    const refetch = useCallback(() => setReloadKey((key) => key + 1), []);

    // Поля фильтра — отдельными переменными: в зависимостях эффекта должны лежать простые
    // значения, которые линтер умеет проверять статически.
    const hasFilter = filter !== null;
    const menuId = filter === null ? null : filter.menuId;
    const createdAfter = filter === null ? null : filter.createdAfter;
    const createdBefore = filter === null ? null : filter.createdBefore;
    const isPaid = filter === null ? null : filter.isPaid;
    const statuses = filter === null ? "" : filter.statuses.join(",");
    const deliveryTypes = filter === null ? "" : filter.deliveryTypes.join(",");
    const paymentTypes = filter === null ? "" : filter.paymentTypes.join(",");

    useEffect(() => {
        // Меню не выбрано — запрос без него бессмыслен. Загрузку снимаем: у филиала может
        // не быть ни одного меню, и экран должен показать пустое состояние, а не спиннер.
        if (filter === null) {
            setOrders([]);
            setTotal(0);
            setLoading(false);
            return undefined;
        }

        // ignore-флаг: ответ по прошлому фильтру или странице не трогает стейт.
        let ignore = false;

        setLoading(true);
        setError(false);

        getOrders(filter, skip, limit)
            .then((result) => {
                if (ignore) return;
                if (result) {
                    setOrders(result.items);
                    setTotal(result.total);
                } else {
                    setError(true);
                    setOrders([]);
                    setTotal(0);
                }
            })
            .catch(() => {
                if (ignore) return;
                setError(true);
                setOrders([]);
                setTotal(0);
            })
            .finally(() => {
                if (ignore) return;
                setLoading(false);
            });

        return () => {
            ignore = true;
        };
        // Сам filter в зависимостях не нужен: он пересобирается на каждый рендер вызывающего,
        // а меняется ровно тогда, когда меняется одно из полей ниже.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        hasFilter,
        menuId,
        createdAfter,
        createdBefore,
        isPaid,
        statuses,
        deliveryTypes,
        paymentTypes,
        skip,
        limit,
        reloadKey,
    ]);

    return { orders, total, loading, error, refetch };
};
